import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, RefreshCw, Search, ArrowLeft, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesChat = ({ onNavigate }) => {
  const [conversations, setConversations] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  // Pause the auto-refresh loops while the user is actively typing a reply
  // or has the input focused. Without this, the every-5s message poll
  // would clobber React state, snap scroll to the bottom, and make typing
  // a reply impossible (user-reported May 2026).
  const isComposingRef = useRef(false);
  // Track the last fetched message count so we only auto-scroll-to-bottom
  // when NEW messages actually arrive — not on every poll tick that
  // re-fetches the same data.
  const lastMessageCountRef = useRef(0);

  // Fetch all conversations (grouped by customer)
  const fetchConversations = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/sms/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const next = data.conversations || [];
        // Only update state if the conversation list has actually changed
        // (different length OR any conversation's unread_count / last
        // message changed). Otherwise React re-renders the whole list +
        // resets scroll position on the sidebar.
        setConversations((prev) => {
          if (prev.length !== next.length) return next;
          const changed = next.some((c, i) => {
            const p = prev[i];
            return !p
              || p.customer_id !== c.customer_id
              || p.unread_count !== c.unread_count
              || p.last_message !== c.last_message
              || p.last_message_time !== c.last_message_time;
          });
          return changed ? next : prev;
        });
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for selected customer
  const fetchMessages = async (customerId) => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/sms/messages?customer_id=${customerId}&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const next = (data.messages || []).slice().reverse();
        // Same change-detection guard as fetchConversations: only update
        // state when the data has actually changed. Otherwise the
        // every-poll re-render trashes the scroll position and (more
        // critically) interrupts the user's typing because React
        // reconciles around them.
        setMessages((prev) => {
          if (prev.length !== next.length) return next;
          const lastPrev = prev[prev.length - 1];
          const lastNext = next[next.length - 1];
          // Same length + same last message ID + same body? → no change.
          if (
            lastPrev && lastNext
            && lastPrev.id === lastNext.id
            && lastPrev.message === lastNext.message
          ) {
            return prev;
          }
          return next;
        });
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  useEffect(() => {
    fetchConversations();
    // Poll the conversation list every 60s (was 10s). Sidebar updates
    // don't need to be near-real-time; the active conversation has its
    // own faster poll below.
    const interval = setInterval(() => {
      // Don't even fire the request while the user is composing — saves
      // a re-render that, while now guarded against, still costs a
      // network round-trip for nothing.
      if (isComposingRef.current) return;
      fetchConversations();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      lastMessageCountRef.current = 0;  // reset for new conversation
      fetchMessages(selectedCustomer.customer_id);
      // Poll the active conversation every 15s (was 5s). 5s was
      // aggressive enough to make replying impossible — every 5s the
      // messages array was being replaced, scroll snapping to bottom,
      // and the textarea losing usability. 15s feels live without
      // fighting the user; the polling is paused entirely while
      // composing.
      const interval = setInterval(() => {
        if (isComposingRef.current) return;
        fetchMessages(selectedCustomer.customer_id);
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    // Only scroll to the bottom when the message COUNT changes
    // (i.e. a new message has arrived or been sent), not on every
    // re-render. Previously this fired on every state set, snapping
    // scroll constantly during polls.
    if (messages.length !== lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedCustomer) return;
    
    setSending(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/sms/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_phone: selectedCustomer.phone,
          message: newMessage.trim(),
          customer_id: selectedCustomer.customer_id
        })
      });
      
      if (res.ok) {
        setNewMessage('');
        // Clear the composing lock now that the message is sent —
        // otherwise the next polls would still be paused indefinitely.
        isComposingRef.current = false;
        fetchMessages(selectedCustomer.customer_id);
        toast.success('Message sent');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(c => 
    c.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone?.includes(searchQuery)
  );

  // Mobile: Show conversation list or chat
  const showChat = selectedCustomer !== null;

  return (
    <div className="h-[calc(100vh-120px)] flex bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Conversations List */}
      <div className={`${showChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 border-r border-zinc-200`}>
        <div className="p-4 border-b border-zinc-200">
          <h2 className="text-lg font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <MessageSquare className="text-emerald-500" size={20} />
            Messages
          </h2>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-zinc-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="animate-spin text-zinc-400" size={24} />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-zinc-500 text-sm">
              No conversations yet
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <div
                key={conv.customer_id || conv.phone}
                onClick={() => setSelectedCustomer(conv)}
                className={`p-4 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${
                  selectedCustomer?.customer_id === conv.customer_id ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-zinc-900 truncate">{conv.customer_name || 'Unknown'}</p>
                      <span className="text-xs text-zinc-400">{conv.last_message_time}</span>
                    </div>
                    <p className="text-sm text-zinc-500 truncate">{conv.last_message}</p>
                    {conv.unread_count > 0 && (
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-500 text-white text-xs rounded-full mt-1">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${showChat ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {selectedCustomer ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
              <button
                onClick={() => setSelectedCustomer(null)}
                className="md:hidden p-2 hover:bg-zinc-100 rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <User size={18} className="text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-zinc-900">{selectedCustomer.customer_name}</p>
                <p className="text-sm text-zinc-500 flex items-center gap-1">
                  <Phone size={12} /> {selectedCustomer.phone}
                </p>
              </div>
              <button
                onClick={() => fetchMessages(selectedCustomer.customer_id)}
                className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500"
              >
                <RefreshCw size={18} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.direction === 'inbound' || msg.message_type === 'incoming' ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[75%] p-3 rounded-2xl ${
                      msg.direction === 'inbound' || msg.message_type === 'incoming'
                        ? 'bg-white border border-zinc-200 rounded-tl-sm'
                        : 'bg-emerald-500 text-white rounded-tr-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-all">
                      {(() => {
                        const text = msg.message || '';
                        const urlRe = /(https?:\/\/[^\s]+)/g;
                        const parts = text.split(urlRe);
                        const isOutbound = !(msg.direction === 'inbound' || msg.message_type === 'incoming');
                        return parts.map((part, i) => {
                          if (/^https?:\/\//.test(part)) {
                            return (
                              <a
                                key={i}
                                href={part}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`underline font-medium ${isOutbound ? 'text-white' : 'text-emerald-700'}`}
                                data-testid={`chat-link-${msg.id}`}
                              >
                                {part}
                              </a>
                            );
                          }
                          return <span key={i}>{part}</span>;
                        });
                      })()}
                    </p>
                    <p className={`text-xs mt-1 ${
                      msg.direction === 'inbound' || msg.message_type === 'incoming' ? 'text-zinc-400' : 'text-emerald-100'
                    }`}>
                      {new Date(msg.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-zinc-200 bg-white">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // Mark as composing while there's text in the box.
                    // This pauses both polls so React state doesn't get
                    // clobbered mid-typing. We also pause on focus
                    // (handler below) so even an empty-but-focused input
                    // wins — covers the case where the user has tapped
                    // the input but not started typing yet.
                    isComposingRef.current = e.target.value.length > 0;
                  }}
                  onFocus={() => { isComposingRef.current = true; }}
                  onBlur={() => {
                    // Keep the lock for 2s after blur so a stray focus
                    // event (mobile keyboard dismissal can briefly
                    // refocus the page) doesn't immediately trigger a
                    // poll-and-rerender. After that, only the
                    // text-in-box check keeps the lock.
                    setTimeout(() => {
                      isComposingRef.current = newMessage.length > 0;
                    }, 2000);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-3 bg-zinc-100 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="chat-reply-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="p-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 text-white rounded-full transition-colors"
                  data-testid="chat-send-btn"
                >
                  {sending ? <RefreshCw className="animate-spin" size={20} /> : <Send size={20} />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-zinc-400">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-3 opacity-50" />
              <p>Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesChat;
